/* eslint-disable no-unused-vars */
/* eslint-disable no-console */

// Future Developer Note:
// Any imports you load here go into the default bundle.
// In other words, you'll make the time bundles boot longer and larger.
// The goal with this file is to keep the imports to a minimum
// and NOT import any app-specific code here, because we may redirect
// and not boot the app. We use a dynamic import() below to actually
// load the app - this lets webpack tree-shake and make a separate
// physical bundle for this file and for the app bundle, so it can
// just load this file without loading the app, saving time and bandwidth.
import React from 'react';

// Need capacitor to detect what type of platform (web/ios/android) we're on
import { Capacitor } from '@capacitor/core';

// Utilities and app config
import styled from 'styled-components';
import { later } from 'shared/utils/later';
import AppConfig from 'shared/config-public';

// Managed apps custom BootAnims must be explicitly imported,
// because I don't want to do dynamic imports here in the shim.
import { BootAnim as MatmBootAnim } from 'managed-apps/meet-at-the-mirror/frontend/BootAnim';
import { BootAnim as PrecinaBootAnim } from 'managed-apps/precina-patients/frontend/BootAnim';
import { BootAnim as ASCBootAnim } from 'managed-apps/avatar-sharing-christ/frontend/BootAnim';
import { BootAnim as BusinessCoachBootAnim } from 'managed-apps/business-coach/frontend/BootAnim';

// This is the ONLY app component we import and we "know"
// it has limited child imports so it is light and can render
// quickly while the rest of the app boots.
import { BootAnim } from './BootAnim';
import {
	getBundleAppConfig,
	getBundleAppId,
} from './utils/getBundleAppId';
import { isBackgroundOtaEnabled } from './backgroundOtaFlag';

const CustomBootAnims = {
	default: BootAnim,
	'com.meetatthemirror': MatmBootAnim,
	'com.precina.app': PrecinaBootAnim,
	'com.precina.admin': PrecinaBootAnim,
	'com.asc': ASCBootAnim,
	// 'com.businesscoach': BusinessCoachBootAnim, // !DEPRECATED
	'com.johnoberg.app': BusinessCoachBootAnim,
};

export const getCustomBootAnim = () => {
	const bundleAppId = getBundleAppId();
	const hasCustomBootAnim = !!CustomBootAnims[bundleAppId];
	if (hasCustomBootAnim) {
		console.log(
			`[getCustomBootAnim] localAppShim: Booting custom boot anim for ${bundleAppId}`,
		);
	} else {
		console.log(
			`[getCustomBootAnim] localAppShim: Booting default boot anim for ${bundleAppId}`,
		);
	}
	return hasCustomBootAnim
		? CustomBootAnims[bundleAppId]
		: CustomBootAnims.default;
};

// Simple check routine to use the native HTTP plugin to
// check the given URL for a 2xx or 3xx status code.
// If 2x or 3x found, all good. If anything else - bad.
// Also will timeout after 2000ms (default)
async function checkUrl(url, timeout = 2000) {
	return new Promise(async (resolve) => {
		let done = false;
		setTimeout(() => {
			if (!done) {
				resolve({
					success: false,
					message: `Timed out requesting ${url} after ${timeout} milliseconds`,
				});
			}
		}, timeout);

		// const fullResponse = await Http.get({ url });
		const fullResponse = await fetch(url).catch((error) => {
			return { error, status: 500 };
		});

		done = true;

		const { status, error } = fullResponse;

		const responseCodeClass = `${status}`.slice(0, 1);
		console.log(
			`[Check URL (${url})] fetch response:`,
			responseCodeClass,
			fullResponse,
		);

		resolve({
			success: !!['2', '3'].includes(responseCodeClass),
			message: error?.message || `HTTP ${status} response`,
		});
	});
}

const StyledPageContainer = styled.div`
	color: white;
	display: flex;
	flex-direction: column;
	align-items: center;
	height: 100%;
	position: relative;

	color: #6a2a5b;
	background: white;

	background-color: white;
	box-shadow: 0 0 0 6px #b5479b, 0 1px 2px 0 #6a2a5b;
	max-height: 650px;
	max-width: 477px;

	overflow: auto;

	border-radius: 1rem;

	max-width: 50%;
	margin: 3rem 1rem 3rem;

	padding: 40px;

	text-align: center;

	&.loading {
		transform: translateX(-300%);
	}

	.error {
		margin: 1rem;
		font-weight: bold;
	}

	@media screen and (max-width: 540px) {
		max-width: 100%;
	}

	.message {
		font-size: 65%;
	}
`;

function OfflineMessage({ message }) {
	return (
		<StyledPageContainer>
			<h1>You're offline</h1>
			{!!message && <div className="message">{message}</div>}

			<h4>
				Please check your internet connection and then re-open the
				app
			</h4>
		</StyledPageContainer>
	);
}

// Dedicated render function so we can override to mock for testing if desired
let renderComponentInDom = (Component) => {
	renderComponentInDom.root.render(<Component />);
	// ReactDOM.render(<Component />, document.getElementById('root'));
};

// Dedicated accessor for mocking if needed
let getCurrentPlatform = () => Capacitor.getPlatform();

/**
 * This is the main "exit" point for the localAppShim.js, it returns
 * control of the current web view (and DOM) to our App.js code.
 *
 * Think of the flow as going:
 * ```
 * localAppShim > redirectOrBoot > bootLocalApp
 * ```
 *
 * Where `redirectOrBoot`, if not redirecting, calls `bootLocalApp`
 * at a couple different exit points to start booting the actual app.
 *
 * This takes advantage of ES6's dynamic `import()`s and webpacks
 * tree-shaking/bundle-splitting to delay ANY parsing or execution of
 * the rest of the app until we call this function.
 *
 * This ensures the update code (this file) has complete control over the
 * DOM and does not have to worry about setting some flag to stop
 * the app from rendering while still updating.
 *
 * Using the import() also allows us to instead render a lightweight
 * `BootAnim` while waiting on heuristic decisions
 */
// Kick off the admin App.js chunk import immediately so it downloads in
// parallel with the boot animation render and the managed-app preload,
// rather than waiting for later()'s ~100ms initial delay. bootLocalApp()
// below will `await` this promise (webpack resolves the same chunk).
let adminAppImportPromise = null;
function warmAdminAppImport() {
	if (typeof window === 'undefined') return;
	if (!adminAppImportPromise) {
		window.__matmBootMark?.('shim_warm_admin_app_start');
		adminAppImportPromise = import(
			/* webpackChunkName: "admin-app" */ './App'
		).then((m) => {
			window.__matmBootMark?.('shim_warm_admin_app_done');
			return m;
		});
	}
}

async function bootLocalApp() {
	const startTime = Date.now();
	// console.log(`localAppShim: Booting local bundle from App.js..`);
	window.__matmBootMark?.('shim_boot_local_app_start');

	const bundleAppId = getBundleAppId();
	if (bundleAppId) {
		if (AppConfig.buildEnv !== 'dev') {
			console.log(
				`localAppShim: Booting local bundle and setting 'window.bundleAppId' to: ${bundleAppId}`,
			);
		}
		// Ideally, picked up by App.js for something meaningful, still have to write that code...
		window.bundleAppId = bundleAppId;
	}

	let error;
	const appPromise = adminAppImportPromise || import('./App');
	const { default: App, ...props } =
		(await appPromise.catch((ex) => {
			error = ex;
		})) || {};
	window.__matmBootMark?.('shim_app_import_done');

	if (!App) {
		// Basically an unrecoverable error
		console.error(
			`Unrecoverable Error importing App, the application will fail:`,
			{
				message: error?.message,
				stack: error?.stack,
				props,
			},
		);

		// Chunk errors are usually deploy skew (a reload fetches the new chunk index)
		// or a dropped connection. BC-4024: this used to call location.reload() with
		// NO cap, which reloaded every ~120s forever on a persistently failing link.
		// The inline boot watchdog (src/boot/bootWatchdog.inline.js) reloads at most
		// MAX_AUTO_RELOADS times, sharing AppErrorBoundary's counter, then shows an
		// honest "failed to load" screen.
		const watchdog = window.__vayaBoot;
		if (
			`${error?.message}`.includes('chunk') &&
			watchdog?.reportChunkFailure
		) {
			console.warn(
				`Chunk error importing App, handing to the boot watchdog:`,
				error.message,
			);
			watchdog.reportChunkFailure(error);
		} else {
			// Not a chunk error, or a shell with no watchdog: show the error rather
			// than reload blindly, and disarm the watchdog's "still loading" notice.
			window.__vayaBoot?.markMounted?.();
			renderComponentInDom(() => (
				<code className="break-all-safe text-red-800">
					{error?.message}
				</code>
			));
		}
		return;
	}

	const endTime = Date.now();
	const loadTime = endTime - startTime;
	if (AppConfig.buildEnv !== 'dev') {
		console.log(
			`localAppShim: App.js loaded, mounting on DOM and returning control to the app. (Bundle loaded in ${loadTime} ms)`,
		);
	}

	// BC-4024: App's chunk is in hand, so boot succeeded. Disarm the inline boot
	// watchdog's failure detection and its "still loading" notice.
	window.__vayaBoot?.markMounted?.();

	// Mount <App> in the DOM and the app will continue it's normal boot from there
	renderComponentInDom(App);
}

/**
 * This is the core update heuristic, it primarily deals with
 * determining the proper remote URL to use and then determines if the
 * device has network access and if so, redirects the web view to that
 * new remote URL. There's far more nuance to it, but you can read
 * the code yourself - that's the big picture intent, anyway.
 */
async function redirectOrBoot() {
	// if (Date.now()) {
	// 	console.log('redirectOrBoot: forcing local boot');
	// 	await bootLocalApp();
	// 	return;
	// }

	// Just stringify for ease of use below
	const currentUrl = `${window.location.href}`;

	const bundleAppId = getBundleAppId();
	console.log(
		`localAppShim: Booting local bundle and setting 'window.bundleAppId' to: ${bundleAppId}`,
	);

	const bundleAppConfig = getBundleAppConfig();
	console.log(`localAppShim: bundleAppConfig:`, bundleAppConfig);

	const { server: serverData } = bundleAppConfig || {};
	const { url: fullServerUrl } = serverData || {};
	let serverUrl;
	if (fullServerUrl) {
		try {
			serverUrl = new URL(fullServerUrl);
			serverUrl.pathname = '';
			serverUrl.search = '';
			serverUrl.hash = '';
			serverUrl = serverUrl.toString().replace(/\/$/, '');
			console.log(
				`localAppShim: Simplified serverUrl from '${fullServerUrl}' to '${serverUrl}'`,
				serverUrl,
			);
		} catch (error) {
			console.error(
				`localAppShim: Error parsing server URL from config:`,
				error,
			);
		}
	}

	// Grab configs from app.config.js
	// let { frontendV2Root: remoteBundleUrl } = AppConfig;

	// For Android, we've already updated capacitor.config.json to ALLOW the vayavalet.io domain.
	// However, iOS was submitted for review before that domain was added, so we need to use the
	// admin.vayadriving.com domain for iOS since the bundle included in that binary
	// has a localAppShim.js that will route to the admin.vayadriving.com domain.
	// let remoteBundleUrl =
	// 	getCurrentPlatform() === 'android'
	// 		? AppConfig.frontendValetApp // should be app.vayavalet.io
	// 		: AppConfig.frontendV2Root; // should be admin.vayadriving.com

	// Hardcode for fix for both ios and android until we can diagnose why
	// app.vayavalet.io is making people pop out into a webbrowser on both platforms later.
	let remoteBundleUrl = serverUrl || 'https://admin.vayadriving.com';

	// For building a test copy locally
	if (process.env.REACT_APP_LOCAL_NGROK_URL) {
		remoteBundleUrl = process.env.REACT_APP_LOCAL_NGROK_URL;
	}

	// onlineCheckUrl is used to check online status - if we succeed in fetching
	// this URL in a reasonable amount of time, we assume we're online
	// and that the remote bundle is (likely) newer, so we can redirect
	// to remote bundle without booting/mounting the local bundle
	const onlineCheckUrl = `${remoteBundleUrl}/index.html`;

	// Stop from looping - if currentUrl is already pointed at remoteBundleUrl,
	// then boot the current bundle
	if (
		currentUrl.includes(remoteBundleUrl) ||
		// React dev server for testing, because in dev, remoteBundleUrl is tunnel (ngrok),
		// so it wouldn't match localhost (.dev.frontend)
		currentUrl.includes('.ngrok.io')
	) {
		console.log(
			`localAppShim: Running on remote-ish URL, not checking further`,
			{
				currentUrl,
				remoteBundleUrl,
			},
		);
		bootLocalApp();
		return;
	}

	console.log(`localAppShim: resolved urls:`, {
		onlineCheckUrl,
		remoteBundleUrl,
	});

	if (!remoteBundleUrl) {
		// This should never happen, but guard against weird errors if onlineCheckUrl
		// is falsey by showing our own error so we have more context for debugging if needed
		console.error(
			`AppUpdateUrl: no 'clientHost' in AppConfig to check for online status, assuming NOT online and booting local bundle`,
		);
		bootLocalApp();
		return;
	}

	// If we have a URL, means we have a valid env selection
	// and we are NOT running on a remote URL, so check to see if we're online
	// so we can redirect
	const onlineCheckUrlWithCacheBust = `${onlineCheckUrl}?_=${Date.now()}`;

	// Date is present to do cache-busting on load
	// Key '_updatedAt' is not presently used at this moment, other than cache-bust-holder
	const redirectUrl = `${remoteBundleUrl}?_updatedAt=${Date.now()}&bundleAppId=${getBundleAppId()}`;

	// For debugging...
	const ignoreUpdatesFlag = `${currentUrl}`.includes('_ignoreUpdates');

	// MATM-1269 (RN-prep Stage 1a): flag-guarded NON-BLOCKING update check.
	// When enabled, boot the local bundle IMMEDIATELY so the first boot is the
	// real boot — first paint is never blocked by the reachability round-trip —
	// then probe reachability out-of-band. Actually SWAPPING the served bundle
	// (so a fetched update lands on next launch, replacing the remote-redirect
	// model) is device-tested work tracked in bctask_rtojiqsrqdz8rfxqdnkrxr8vm
	// (needs @capgo/capacitor-updater or @capacitor/live-updates + real-device
	// before/after measurement) and is intentionally NOT done here.
	//
	// DEFAULT OFF (see isBackgroundOtaEnabled). Off => the synchronous path below
	// runs unchanged, so this adds no behavior change to any shipped bundle.
	if (isBackgroundOtaEnabled(currentUrl)) {
		console.log(
			`localAppShim: backgroundOta enabled — booting local bundle first, probing reachability in the background (no synchronous redirect)`,
			{ currentUrl, remoteBundleUrl, redirectUrl },
		);

		// First boot IS the real boot — never block first paint.
		bootLocalApp();

		// Fire-and-forget reachability probe. Does NOT redirect or swap the
		// bundle (see task note above); it only surfaces reachability for
		// telemetry / future next-launch staging.
		later(async () => {
			const { success, message } = await checkUrl(
				onlineCheckUrlWithCacheBust,
			);
			console.log(
				`localAppShim: backgroundOta reachability probe ${
					success ? 'ok' : 'failed'
				}`,
				{ success, message },
			);
		});

		return;
	}

	// This timeout is indeed possible on slow networks or busy booting.
	// If we fail here, assume offline and don't redirect to remote
	const { success: urlCheckSuccess, message: urlCheckMessage } =
		await checkUrl(onlineCheckUrlWithCacheBust);

	// const urlCheckSuccess = false;
	// const urlCheckMessage = 'Testing';

	// If error (or timeout), log and render an error message in the DOM
	if (!urlCheckSuccess) {
		// Failure fetching, might be offline, can't do anything in the MP if offline,
		// so just render error messages
		console.error(
			`localAppShim: Error fetching ${onlineCheckUrlWithCacheBust}:`,
			{
				message: urlCheckMessage,
			},
		);

		// BC-4024: an honest message now owns the screen; disarm the boot
		// watchdog so its "still loading" notice cannot cover it.
		window.__vayaBoot?.markMounted?.();
		renderComponentInDom(() => (
			<OfflineMessage message={urlCheckMessage} />
		));
		return;
	}

	// We must have set this flag for debugging, so don't actually redirect so we
	// can inspect console output
	if (ignoreUpdatesFlag) {
		console.log(
			`index.updates: found _ignoreUpdates in the URL, stopping and running app`,
			{
				currentUrl,
				remoteBundleUrl,
				redirectUrl,
				ignoreUpdatesFlag,
			},
		);

		bootLocalApp();
		return;
	}

	console.log(
		`localAppShim: Online and appear to not be up-to-date, so going to redirect below...`,
		{
			currentUrl,
			remoteBundleUrl,
			redirectUrl,
			ignoreUpdatesFlag,
		},
	);

	// Exit the app and reload.
	window.location.href = redirectUrl;
}

// Preload a managed-app frontend chunk based on the current URL so its JS
// arrives in parallel with the admin App.js import rather than waiting
// until after the admin app has mounted and matched the `/a/:slug/*` route.
// Also preloads the PlatformAppShortcut chunk (the admin-app page that
// hosts the managed app), since the admin-app router lazy-loads it.
// Webpack magic comments keep these out of the shim's initial chunk.
function preloadManagedAppChunkFromUrl() {
	if (typeof window === 'undefined') return;
	const match = /^\/(?:a|app)\/([^/?#]+)/.exec(window.location.pathname);
	if (!match) return;
	const slug = match[1];
	window.__matmBootMark?.('shim_preload_start', { slug });

	// Preload PlatformAppShortcut — the admin-app page component that hosts
	// every /a/:slug/* route. Lazy in admin App.js so warming it here saves
	// a chunk load after adminapp_App_render.
	import(
		/* webpackChunkName: "platform-app-shortcut" */ './pages/platform/app-shortcut'
	).catch(() => {});

	let p;
	if (slug === 'meet-at-the-mirror') {
		p = import(
			/* webpackChunkName: "matm-app" */ 'managed-apps/meet-at-the-mirror/frontend/App'
		);
		// Also warm the ChatsPage route chunk. It is a lazyWithPreload route
		// (see managed-apps/meet-at-the-mirror/frontend/appRoutes.js) so it is
		// NOT in the matm-app chunk above — a cold deep-link into a thread (e.g.
		// a tapped push notification landing on /a/meet-at-the-mirror/chats/...)
		// would otherwise pay a separate chunk fetch before the thread can
		// render. Preloading here overlaps that fetch with the app boot.
		import(
			/* webpackChunkName: "matm-chats" */ 'managed-apps/meet-at-the-mirror/frontend/pages/chat/ChatsPage'
		).catch(() => {});
	}
	if (p) {
		p.then(
			() => window.__matmBootMark?.('shim_preload_done', { slug }),
			() => {},
		);
	}
}

export default async function localAppShim({
	root,
	disableLater = false,
}) {
	renderComponentInDom.root = root;
	renderComponentInDom(getCustomBootAnim());
	warmAdminAppImport();
	// In prod, fire both in parallel for max boot speed. In dev, serialize:
	// rsbuild's lazy-compilation races on shared modules (PushNotifyService,
	// PlatformEnums) when two chunks request them concurrently, producing
	// "factory is undefined" under higher-latency links (e.g. ngrok).
	if (AppConfig.buildEnv === 'dev') {
		adminAppImportPromise?.finally(() =>
			preloadManagedAppChunkFromUrl(),
		);
	} else {
		preloadManagedAppChunkFromUrl();
	}

	// Just for testing custom boot anims
	// if (Date.now()) {
	// 	return;
	// }

	// Move out of event loop so boot anim can render before we do other things
	const internalUpdate = async () => {
		// // Forcing to boot local bundle for Android testing
		// if (Date.now()) {
		// 	await bootLocalApp();
		// 	return;
		// }

		if (getCurrentPlatform() === 'web') {
			// If not running in a native context, nothing to do,
			// so just boot current bundle. Await to ensure
			// any thrown errors caught and logged by later()
			await bootLocalApp();
			return;
		}

		// Execute our heuristic code. Await to ensure
		// any thrown errors caught and logged by later()
		await redirectOrBoot();
	};

	if (disableLater) {
		await internalUpdate();
		return;
	}

	later(internalUpdate);
}
